import { setupSardine, updateSardineConfig } from '@sardine-ai/web-sdk';

import * as datadogRum from 'lib/telemetry/datadogRum';
import { createSardineSession } from 'transport/domains/auth';
import config from 'utils/config';

// Must match the backend's sardine.FlowName values — the flow name is what
// distinguishes these events to Sardine's rules, so a mismatch here means the
// SDK's biometrics land on a different flow than the one the backend scores.
type SardineFlow =
    | 'login'
    | 'signup'
    | 'kyc'
    | 'deposit'
    | 'withdrawal'
    | 'profile_change'
    | 'email_change'
    | 'phone_change'
    | 'mfa_change'
    | 'password_reset';

// Every silent failure path here produces a backend Sardine row that's missing
// the corresponding event — which shows up in support as "no Sardine data for
// this user/login/deposit". Surface each one as a queryable RUM action so we
// can attribute missing-data complaints to a specific failure class.
function recordSardineEvent(
    name:
        | 'init_failed'
        | 'session_fetch_failed'
        | 'configure_failed'
        | 'session_unavailable'
        | 'session_consumed_refetch'
        | 'flow_stale'
        | 'biometrics_session_drift'
        | 'sdk_script_loaded'
        | 'sdk_script_error'
        | 'sdk_script_stalled'
        | 'non_error_rejection',
    attrs: Record<string, string | number | boolean>,
): void {
    datadogRum.addAction(`sardine.${name}`, attrs);
}

// Sardine's SDK rejects with plain objects (e.g. {status: 'error', error: {}}),
// which Datadog reports as ungroupable 'Provided {json}' / 'No stack' events —
// and custom-source errors bypass the beforeSend noise filter. So only real
// Error instances go through addError; non-Error rejections are downgraded to
// a warning-level RUM action that stays queryable without polluting error tracking.
function reportSardineError(
    err: unknown,
    source: 'sardine-init' | 'sardine-config',
    extraAttrs: Record<string, string | number | boolean> = {},
): void {
    if (err instanceof Error) {
        datadogRum.addError(err, { source });
        return;
    }
    let detail: string;
    try {
        detail = JSON.stringify(err) ?? String(err);
    } catch {
        detail = String(err);
    }
    recordSardineEvent('non_error_rejection', { source, detail, ...extraAttrs });
}

function getHttpErrorStatus(error: unknown): number | undefined {
    if (typeof error !== 'object' || error === null || !('response' in error)) return undefined;

    const { response } = error as { response?: unknown };
    if (typeof response !== 'object' || response === null || !('status' in response)) return undefined;

    const { status } = response as { status?: unknown };
    return typeof status === 'number' ? status : undefined;
}

// Tracks the session the SDK is currently bound to via a successful
// updateSardineConfig — distinct from activeSessionKey, which is set
// the moment the session is fetched (before SDK rebind). When the two
// diverge, biometrics for the key returned to the backend may be empty
// or attached to the wrong Sardine session.
let sdkBoundSession: string | null = null;

let sdkSetupPromise: Promise<void> | null = null;
let prefetchedSession: { session_key: string; client_id: string } | null = null;
let prefetchPromise: Promise<void> | null = null;

// Resolves when the injected Sardine loader script fires onerror — a definitive failure
// (blocked by an ad blocker / privacy browser / webview). The bind can't succeed once
// this happens, so waitForSardineBound races it to bail immediately for these users
// instead of waiting out the full configure timeout. A stall is deliberately NOT treated
// as failure here (see beaconSardineScriptLoad) since it's ambiguous and permanent.
let markSdkScriptFailed!: () => void;
const sdkScriptFailed = new Promise<void>((resolve) => {
    markSdkScriptFailed = resolve;
});

// The loader script's last known outcome, surviving flow changes and re-inits within a
// page. Lets beaconSardineScriptLoad skip re-observing a loader that already resolved on an
// earlier setup attempt — a re-init (e.g. logout→login) injects no new script node, so a
// fresh observer would just wait out a false stall.
const SDK_SCRIPT_STATUS_BY_EVENT = {
    sdk_script_loaded: 'loaded',
    sdk_script_error: 'error',
    sdk_script_stalled: 'stalled',
} as const;
let sdkScriptStatus: 'pending' | 'loaded' | 'error' | 'stalled' = 'pending';

// Promise tied to the in-flight bind of the active session, recorded before
// sessionReadyPromise resolves. waitForSardineBound awaits this rather than the global
// configChain: configureFlow only appends to configChain after awaiting ensureSdkSetup,
// so at the moment a caller reads the key and calls waitForSardineBound, configChain may
// not yet include this bind (and would resolve immediately against a stale, resolved one).
let activeBindSessionKey: string | null = null;
let activeBindPromise: Promise<void> = Promise.resolve();

// Tracks the session started by startSardineFlow so fetchSardineSessionKey can reuse it.
let activeSessionKey: string | null = null;
let activeFlow: SardineFlow | null = null;
let activeSessionConsumed = false;
let startFlowPromise: Promise<void> | null = null;
let sessionReadyPromise: Promise<void> | null = null;
// Monotonically increasing ID to detect stale flows (prevents race conditions
// when navigating quickly between pages that start different flows)
let flowGeneration = 0;

// Bumped only by resetSardineState(), i.e. on a session end. Deliberately distinct
// from flowGeneration, which also rotates when a new flow supersedes an older one
// inside a live session — those checks intentionally let already-committed work
// finish. A continuation that outlives a session teardown must never write module
// state back, or the next applicant consumes the previous applicant's session key
// and collects biometrics under it.
let resetGeneration = 0;

function getSardineEnvironment(): 'sandbox' | 'production' {
    return config.frontendEnv === 'production' ? 'production' : 'sandbox';
}

// onerror is opaque for cross-origin scripts, so infer *why* the loader failed: offline
// (navigator.onLine), whether the request reached the network (a resource-timing entry
// exists), and whether the URL is reachable at all (a no-cors fetch — a content blocker
// blocks this too, so a rejection ≈ blocked/unreachable). Runs async so it never delays the
// bind-wait short-circuit. Reads in RUM as @context.online / @context.probe / @context.had_timing.
async function recordSardineScriptError(ms: number, loaderSrc: string): Promise<void> {
    const online = typeof navigator !== 'undefined' ? navigator.onLine : true;
    const hadTiming = loaderSrc.length > 0 && performance.getEntriesByName(loaderSrc).length > 0;
    let probe: 'reachable' | 'failed' | 'skipped' = 'skipped';
    if (loaderSrc && online) {
        const signal =
            typeof AbortSignal !== 'undefined' && 'timeout' in AbortSignal ? AbortSignal.timeout(3000) : undefined;
        try {
            // eslint-disable-next-line no-restricted-globals -- diagnostic probe to a third party (sardine.ai); must not use the WAF-wrapped fetch or send Kalshi credentials
            await fetch(loaderSrc, { mode: 'no-cors', cache: 'no-store', credentials: 'omit', signal });
            probe = 'reachable';
        } catch {
            probe = 'failed';
        }
    }
    recordSardineEvent('sdk_script_error', { ms, online, probe, had_timing: hadTiming });
}

// Watches for the SDK's injected loader script (https://<host>/assets/loader.min.js)
// and records whether it loads, errors (blocked by an ad blocker / privacy browser /
// in-app webview), or stalls. This splits "Sardine SDK timeout" into recoverable-slow
// (loaded, but late) vs unrecoverable-blocked (error) so we can tell whether raising the
// configure timeout actually helps. Uses addEventListener so the SDK's own handlers stay.
function beaconSardineScriptLoad(): void {
    if (typeof document === 'undefined' || typeof MutationObserver === 'undefined') {
        return;
    }
    // A re-init injects no new loader node, so a fresh observer would never fire and would
    // falsely stall. Reuse the outcome from the earlier setup instead of re-observing.
    if (sdkScriptStatus !== 'pending') {
        return;
    }
    const startedAt = performance.now();
    let settled = false;
    let loaderSrc = '';
    const observer = new MutationObserver((mutations) => {
        for (const mutation of mutations) {
            for (const node of mutation.addedNodes) {
                if (node instanceof HTMLScriptElement && node.src.includes('loader.min.js')) {
                    // Found the loader; stop processing DOM churn and let the node's own
                    // load/error events drive the beacon.
                    observer.disconnect();
                    loaderSrc = node.src;
                    node.addEventListener('load', () => finish('sdk_script_loaded'));
                    node.addEventListener('error', () => finish('sdk_script_error'));
                    return;
                }
            }
        }
    });
    const stallTimer = setTimeout(finish, 15000, 'sdk_script_stalled');
    function finish(name: 'sdk_script_loaded' | 'sdk_script_error' | 'sdk_script_stalled'): void {
        if (settled) {
            return;
        }
        settled = true;
        observer.disconnect();
        clearTimeout(stallTimer);
        sdkScriptStatus = SDK_SCRIPT_STATUS_BY_EVENT[name];
        const ms = Math.round(performance.now() - startedAt);
        // Only a definitive load error short-circuits the bind wait. A stall is ambiguous
        // (a slow load that arrives after the beacon window, or a re-init where the loader
        // is already in the DOM so no new node is observed) — treating it as failure would
        // permanently poison waitForSardineBound for every later session in the tab.
        if (name === 'sdk_script_error') {
            // Short-circuit the bind wait now; the async probe records the event with the
            // inferred cause so the short-circuit isn't delayed by the network probe.
            markSdkScriptFailed();
            void recordSardineScriptError(ms, loaderSrc);
            return;
        }
        recordSardineEvent(name, { ms });
    }
    observer.observe(document.documentElement, { childList: true, subtree: true });
}

async function ensureSdkSetup(clientId: string): Promise<void> {
    if (!sdkSetupPromise) {
        sdkSetupPromise = (async () => {
            beaconSardineScriptLoad();
            setupSardine({ clientId, environment: getSardineEnvironment() });
        })().catch((e) => {
            sdkSetupPromise = null;
            throw e;
        });
    }
    return sdkSetupPromise;
}

export function initSardine(): void {
    if (prefetchPromise || sdkSetupPromise) return;
    const resetGen = resetGeneration;
    prefetchPromise = createSardineSession()
        .then(async (data) => {
            // A session that ended while this was in flight already cleared
            // prefetchedSession; restoring it here would hand the next applicant
            // the previous one's session key.
            if (resetGen !== resetGeneration) return;
            prefetchedSession = data;
            await ensureSdkSetup(data.client_id);
        })
        .catch((err) => {
            const status = getHttpErrorStatus(err);
            const reason = status === 429 ? 'rate_limited' : 'unexpected';

            if (status !== 429) reportSardineError(err, 'sardine-init');
            recordSardineEvent('init_failed', {
                message: String(err?.message ?? err),
                reason,
                ...(status === undefined ? {} : { status }),
            });
        });
}

async function obtainSession(): Promise<{ sessionKey: string; clientId: string } | null> {
    // This function mutates two pieces of module state that a teardown replaces —
    // `prefetchPromise` and `prefetchedSession` — so it can only touch what its own
    // generation owns. Otherwise a call that resumes after a teardown clears the
    // *next* applicant's prefetch promise and consumes their prefetched session,
    // which its caller then discards on its own generation check: the new flow has
    // to mint a second session, and collects no biometrics at all if that fails.
    const resetGen = resetGeneration;
    try {
        const awaited = prefetchPromise;
        if (awaited) {
            await awaited;
            // Only retire the promise this call actually awaited. A newer
            // initSardine() may have installed its own in the meantime.
            if (prefetchPromise === awaited) {
                prefetchPromise = null;
            }
        }

        if (resetGen !== resetGeneration) {
            // Bail without consuming anything. Every caller re-checks the
            // generation immediately after this returns and discards the result,
            // so there is nothing to gain by minting a session here either.
            return null;
        }

        if (prefetchedSession) {
            const { session_key, client_id } = prefetchedSession;
            prefetchedSession = null;
            return { sessionKey: session_key, clientId: client_id };
        }

        const data = await createSardineSession();
        return { sessionKey: data.session_key, clientId: data.client_id };
    } catch (err) {
        recordSardineEvent('session_fetch_failed', { message: String((err as Error)?.message ?? err) });
        return null;
    }
}

// Serializes all updateSardineConfig calls — Sardine docs explicitly forbid concurrent
// calls because they trigger a complete SDK reset and can drop biometric collection.
// Typed marker for the configure race timeout so the catch below can tell it
// apart from genuine updateSardineConfig rejections without string matching.
class SardineConfigTimeoutError extends Error {
    constructor() {
        super('Sardine SDK timeout');
        this.name = 'SardineConfigTimeoutError';
    }
}

let configChain: Promise<void> = Promise.resolve();

async function configureFlow(
    clientId: string,
    sessionKey: string,
    flow: SardineFlow,
    customerId?: string,
    flowGen?: number,
): Promise<void> {
    const resetGen = resetGeneration;
    await ensureSdkSetup(clientId);
    configChain = configChain
        .then(async () => {
            // resetSardineState() replaces configChain, so an item queued before a
            // teardown can still be running alongside the next session's chain.
            // Drop it rather than binding the SDK to the ended session's key.
            if (resetGen !== resetGeneration) return;
            // Skip stale queue items from startSardineFlow (flowGen provided) when
            // a newer flow has superseded them — drains immediately instead of
            // holding the chain for up to 10s per item.
            // fetchSardineSessionKey does not pass flowGen (undefined) so its calls
            // always run — they correspond to a user action whose session key is
            // already committed to the backend.
            if (flowGen !== undefined && flowGen !== flowGeneration) return;
            await new Promise<void>((resolve, reject) => {
                const timeoutId = setTimeout(() => reject(new SardineConfigTimeoutError()), 10000);
                updateSardineConfig({
                    sessionKey,
                    flow,
                    ...(customerId ? { customerId } : {}),
                }).then(
                    () => {
                        clearTimeout(timeoutId);
                        resolve();
                    },
                    (err) => {
                        clearTimeout(timeoutId);
                        reject(err as Error);
                    },
                );
            });
            // Only mark SDK as bound on success and only when we actually called
            // updateSardineConfig — the stale early-return above must not reach
            // this line, otherwise sdkBoundSession gets set for a session that
            // never collected biometrics, defeating drift diagnostics.
            if (resetGen !== resetGeneration) return;
            sdkBoundSession = sessionKey;
        })
        .catch((err) => {
            // The 10s race timeout is an expected failure class (slow/blocked SDK
            // loader), not an actionable error — the configure_failed action below
            // already keeps it queryable at warning level, so skip addError for it.
            if (!(err instanceof SardineConfigTimeoutError)) {
                reportSardineError(err, 'sardine-config', { flow });
            }
            recordSardineEvent('configure_failed', {
                flow,
                message: String((err as Error)?.message ?? err),
            });
        });
    return configChain;
}

// Emit a drift event when fetchSardineSessionKey returns a key that the SDK
// is not bound to. Two reasons this happens in practice:
//   1. configureFlow hasn't completed yet (the key was returned via the fast
//      sessionReadyPromise path before the SDK rebind landed).
//   2. configureFlow failed/timed out — sdkBoundSession stays at the previous
//      value (or null) and the backend gets a key Sardine has no data for.
function recordDriftIfMismatched(returnedKey: string, flow: SardineFlow): void {
    if (!returnedKey || returnedKey === sdkBoundSession) return;
    recordSardineEvent('biometrics_session_drift', {
        flow,
        sdk_state: sdkBoundSession === null ? 'unbound' : 'different_session',
    });
}

/**
 * Start a Sardine flow early (e.g. on page mount) so behavioral biometrics
 * are collected while the user interacts with input fields.
 * The session key is stored so fetchSardineSessionKey can reuse it.
 */
export function startSardineFlow(flow: SardineFlow, customerId?: string): void {
    // If the same flow is already active (or being set up), keep the existing session
    // so biometrics aren't split across sessions when a component remounts (e.g. KYC
    // name step → birthday → address step all share one session).
    if (activeFlow === flow && !activeSessionConsumed && (activeSessionKey || startFlowPromise)) {
        return;
    }

    // Bump generation to invalidate any in-flight flow from a previous page
    const gen = ++flowGeneration;
    activeSessionKey = null;
    activeFlow = flow;
    activeSessionConsumed = false;

    let resolveSessionReady!: () => void;
    sessionReadyPromise = new Promise<void>((r) => {
        resolveSessionReady = r;
    });

    startFlowPromise = (async () => {
        const session = await obtainSession();
        // Check staleness first — if a newer flow has rotated the generation,
        // we don't care whether obtainSession succeeded; the new flow will
        // report its own outcome. Reporting session_unavailable here would
        // taint the diagnostic signal for backend-side missing-data attribution.
        if (gen !== flowGeneration) {
            recordSardineEvent('flow_stale', { flow, had_session: Boolean(session) });
            resolveSessionReady();
            return;
        }
        if (!session) {
            recordSardineEvent('session_unavailable', { phase: 'start_flow', flow });
            resolveSessionReady();
            return;
        }

        activeSessionKey = session.sessionKey;
        activeSessionConsumed = false;

        // Record the bind promise BEFORE resolving session-ready so a caller that reads
        // the key (fetchSardineSessionKey) and then calls waitForSardineBound observes a
        // promise tied to THIS bind, not the already-resolved global configChain.
        activeBindSessionKey = session.sessionKey;
        activeBindPromise = configureFlow(session.clientId, session.sessionKey, flow, customerId, gen).catch(() => {
            // SDK config failure is non-fatal — session key is still valid
        });

        resolveSessionReady();
        await activeBindPromise;
    })();
}

/**
 * Outcome of a session-key fetch. The two failure modes are deliberately distinct
 * because callers must react to them in opposite ways:
 *
 * - `{ ok: true, key }` — go ahead. `key` is `''` when Sardine is simply
 *   unavailable (outage, rate limit, blocked loader); the backend skips Sardine on
 *   an empty key, so the caller's own operation must still proceed. Failing closed
 *   on a Sardine outage would take down deposits, logins and password resets.
 * - `{ ok: false }` — the session that started this fetch has since ended
 *   (`resetSardineState`, i.e. logout or forced disconnect). The caller must abort:
 *   it captured a user id before awaiting, and issuing that request now sends the
 *   ended user's operation. If someone else has signed in meanwhile, the resulting
 *   401 is read against *their* session and disconnects them.
 *
 * Modelled as a discriminated union rather than an empty string so the two cases
 * cannot be conflated, and rather than a rejection so a caller's `try/catch` can't
 * swallow the abort and a fire-and-forget caller can't turn it into an unhandled
 * rejection. The compiler makes every call site say which case it is handling.
 */
export type SardineSessionKeyResult = { ok: true; key: string } | { ok: false };

const SARDINE_SESSION_INVALIDATED: SardineSessionKeyResult = { ok: false };
const sardineKey = (key: string): SardineSessionKeyResult => ({ ok: true, key });

export async function fetchSardineSessionKey(
    flow: SardineFlow = 'login',
    customerId?: string,
    // When the caller will await waitForSardineBound after this, defer the drift decision
    // to after that wait — the bind may still land, and recording drift here would count a
    // recovered session as missing in Datadog. waitForSardineBound records it post-wait.
    deferDrift = false,
): Promise<SardineSessionKeyResult> {
    // Captured before any await. Every await below can straddle a session
    // teardown, and resuming afterwards would write the ended applicant's session
    // into activeSessionKey/activeFlow and rebind the SDK to it — collecting this
    // tab's later activity under that session.
    const resetGen = resetGeneration;
    try {
        // Wait for the session key only, not configureFlow (which may time out).
        if (sessionReadyPromise && activeFlow === flow) {
            await sessionReadyPromise;
            if (resetGen !== resetGeneration) return SARDINE_SESSION_INVALIDATED;
            if (activeSessionKey && !activeSessionConsumed) {
                activeSessionConsumed = true;
                if (!deferDrift) {
                    recordDriftIfMismatched(activeSessionKey, flow);
                }
                return sardineKey(activeSessionKey);
            }
            // Active session was already consumed by an earlier caller, or
            // startSardineFlow never produced a key — fall through and mint a
            // fresh one. Track this because it means SDK biometrics for the
            // first session are decoupled from the key the backend gets.
            recordSardineEvent('session_consumed_refetch', {
                flow,
                had_active_key: Boolean(activeSessionKey),
            });
        }

        // Fallback: create a new session (only when no active session exists for this flow)
        const session = await obtainSession();
        // Discard a session minted for an applicant whose session has since ended,
        // before it reaches any global state or the SDK.
        if (resetGen !== resetGeneration) return SARDINE_SESSION_INVALIDATED;
        if (!session) {
            // Sardine is unavailable, not invalidated: the caller proceeds keyless.
            recordSardineEvent('session_unavailable', { phase: 'fetch', flow });
            return sardineKey('');
        }

        activeSessionKey = session.sessionKey;
        activeFlow = flow;
        activeSessionConsumed = true;

        try {
            await configureFlow(session.clientId, session.sessionKey, flow, customerId);
        } catch {
            // SDK failures are non-fatal — session key is still valid.
            // configure_failed is already recorded inside configureFlow,
            // and recordDriftIfMismatched below will surface the binding gap.
        }

        // Configuration can stay pending for up to 10s (longer behind the barrier
        // resetSardineState leaves in place), so recheck rather than hand the key
        // back on the far side of a teardown. Callers — twoFactorLogin, the bank and
        // payment actions — resume here with a user id captured before the await, so
        // this is what tells them to abort instead of running the ended session's work.
        if (resetGen !== resetGeneration) return SARDINE_SESSION_INVALIDATED;

        if (!deferDrift) {
            recordDriftIfMismatched(session.sessionKey, flow);
        }
        return sardineKey(session.sessionKey);
    } catch (err) {
        // An unexpected failure is still just "no Sardine data" — fail open so a
        // Sardine problem cannot block the caller's operation.
        recordSardineEvent('session_unavailable', {
            phase: 'fetch',
            flow,
            message: String((err as Error)?.message ?? err),
        });
        return sardineKey('');
    }
}

export function peekSardineSessionKey(): string | null {
    return !activeSessionConsumed && activeSessionKey ? activeSessionKey : null;
}

/**
 * Bounded wait for the SDK to finish binding to `sessionKey` (updateSardineConfig
 * resolved and configureFlow set sdkBoundSession). Resolves immediately when already
 * bound, and always resolves within timeoutMs so it never blocks the user. Returns
 * whether the bind landed — callers submit the key regardless; this only gives an
 * in-flight bind a chance to finish so the backend's Sardine session has device data.
 */
export async function waitForSardineBound(sessionKey: string, flow: SardineFlow, timeoutMs = 10000): Promise<boolean> {
    if (!sessionKey || sdkBoundSession === sessionKey) {
        return sdkBoundSession === sessionKey;
    }
    // Wait on the promise tied to THIS session's bind (set up before session-ready
    // resolved), not the global configChain which may not yet include it. Race a known
    // script failure (bail immediately — the bind can't land) and the timeout cap.
    // Blocked users short-circuit; only genuinely-pending binds wait out the full timeout.
    const bindPromise = activeBindSessionKey === sessionKey ? activeBindPromise : Promise.resolve();
    await Promise.race([bindPromise, sdkScriptFailed, new Promise<void>((resolve) => setTimeout(resolve, timeoutMs))]);
    // Record drift now, reflecting the post-wait state — a bind that landed during the
    // wait correctly produces no drift (fetchSardineSessionKey deferred it to here).
    recordDriftIfMismatched(sessionKey, flow);
    return sdkBoundSession === sessionKey;
}

export function clearSardineFlow(): void {
    activeSessionKey = null;
    activeFlow = null;
    activeSessionConsumed = false;
    startFlowPromise = null;
    sessionReadyPromise = null;
    activeBindSessionKey = null;
    activeBindPromise = Promise.resolve();
}

export function resetSardineState(): void {
    // Clear all state so new callers start fresh. Don't call unbind() — it creates
    // a zombie context that updateSardineConfig tries to reuse via updateConfig()
    // instead of doing a full re-init. Skipping unbind lets the SDK's existing context
    // stay alive; the next updateSardineConfig updates the session key in-place.
    sdkSetupPromise = null;
    // Not Promise.resolve(): an updateSardineConfig already invoked for the ended
    // session is still running, and Sardine forbids concurrent calls — they trigger
    // a full SDK reset and can drop biometric collection. The resetGeneration checks
    // in configureFlow make that item's results inert, but they cannot cancel a call
    // already in flight, so the chain is kept purely as the serialization barrier.
    // It always settles (the configure race caps at 10s and configureFlow's tail
    // .catch swallows), so this cannot wedge the next session's configure.
    configChain = configChain.catch(() => {});
    prefetchedSession = null;
    prefetchPromise = null;
    activeSessionKey = null;
    activeFlow = null;
    activeSessionConsumed = false;
    startFlowPromise = null;
    sessionReadyPromise = null;
    sdkBoundSession = null;
    // Everything clearSardineFlow() drops, dropped here too. These two were the one
    // piece of ended-session state this function left behind — inert in practice, since
    // the next startSardineFlow() overwrites both and waitForSardineBound() already
    // short-circuits when they don't match the key it was handed. Cleared anyway so
    // "nothing from the ended session survives a teardown" holds without an exception,
    // and so the ended session's key isn't left sitting in module memory.
    activeBindSessionKey = null;
    activeBindPromise = Promise.resolve();
    flowGeneration++;
    resetGeneration++;
}
